import type { Microsoft365ImportResultDto } from '@dbrain/shared';
import { Button, IconButton, Icons } from '@dbrain/ui';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { placeLabel, resultView, type Target } from './model.js';

/** An import's answer with what the person asked for: the place and the picked files' names (by position). */
export interface ImportSummary {
  result: Microsoft365ImportResultDto;
  target: Target;
  names: string[];
}

/**
 * MsDurumlar "Belge alınamadı", in the picker: "1 dosya alınamadı — **Maaş tablosu.xlsx**: bu dosyayı görme izniniz
 * kalmamış. Diğer 2 dosya eklendi." and "Tamam". A file already in that place is not a failure: "… zaten … içindeydi".
 * File names are shown as text (they come from Microsoft, written by anyone).
 */
export function ImportResultCard({ summary, maxMb, onDone }: { summary: ImportSummary; maxMb: number; onDone: () => void }) {
  const { t } = useTranslation();
  const titleId = useId();
  const ok = useRef<HTMLButtonElement>(null);
  const view = resultView(summary.result, summary.names, maxMb, t);
  const place = placeLabel(summary.target, t);
  // the card replaces what had the focus (the add button)
  useEffect(() => ok.current?.focus(), []);
  const others = view.added > 0 ? t('microsoft365.files.result.others', { count: view.added }) : null;
  const line = (failure: { name: string; reason: string }) => (
    <>
      <strong>{failure.name}</strong>: {failure.reason}
    </>
  );
  return (
    <section className="m365-result" role="alert" aria-labelledby={titleId} data-testid="m365-import-result">
      <span className="m365-result__icon" aria-hidden="true">
        <Icons.error size={18} />
      </span>
      <div className="m365-result__body">
        <h3 id={titleId} className="m365-result__title">
          {t('microsoft365.files.result.failed', { count: view.failed.length })}
        </h3>
        {view.failed.length === 1 && view.failed[0] ? (
          <p className="m365-result__text" data-testid="m365-import-failure">
            {line(view.failed[0])}
            {others ? ` ${others}` : null}
          </p>
        ) : (
          <>
            <ul className="m365-result__lines">
              {view.failed.map((failure, n) => (
                <li key={n} data-testid="m365-import-failure">
                  {line(failure)}
                </li>
              ))}
            </ul>
            {others ? <p className="m365-result__text">{others}</p> : null}
          </>
        )}
        {view.duplicates > 0 ? <p className="m365-result__text">{t('microsoft365.files.result.duplicates', { count: view.duplicates, place })}</p> : null}
        <div className="m365-result__actions">
          <Button ref={ok} size="sm" variant="secondary" onClick={onDone} data-testid="m365-import-result-ok">
            {t('microsoft365.files.result.ok')}
          </Button>
        </div>
      </div>
    </section>
  );
}

/**
 * The line a page shows after the picker closed on a full success (no mockup): "3 belge eklendi · Ekip: Finans",
 * the files already there, and that a composer's pick is used in its conversation too.
 */
export function ImportNotice({ summary, linked, onClose }: { summary: ImportSummary; linked?: 'home' | 'conversation'; onClose: () => void }) {
  const { t } = useTranslation();
  const place = placeLabel(summary.target, t);
  const added = summary.result.added.filter((item) => !item.duplicate).length;
  const duplicates = summary.result.added.length - added;
  const lead = added > 0 ? t('microsoft365.files.result.added', { count: added, place }) : null;
  const rest = [
    duplicates > 0 ? t('microsoft365.files.result.duplicates', { count: duplicates, place }) : null,
    linked && summary.result.added.length > 0 ? t(linked === 'home' ? 'microsoft365.files.result.linkedHome' : 'microsoft365.files.result.linked') : null,
  ].filter((part): part is string => Boolean(part));
  if (!lead && rest.length === 0) return null;
  return (
    <p className="m365-imported" role="status" data-testid="m365-imported">
      <Icons.done size={16} />
      <span className="m365-imported__text">
        {lead ? <strong>{lead}</strong> : null}
        {rest.length > 0 ? ` ${rest.join(' ')}` : null}
      </span>
      <IconButton size="sm" label={t('common.close')} icon={<Icons.close size={14} />} onClick={onClose} />
    </p>
  );
}
